export type LeadTimeParts = {
  days: number;
  hours: number;
  minutes: number;
};

/** Each DD / HH segment in masked input (0–99). */
export const LEAD_TIME_SEGMENT_MAX = 99;

/** Minutes segment (0–59); values above 59 are reduced to 59. */
export const LEAD_TIME_MINUTES_MAX = 59;

/** Clamp each segment to allowed max (minutes cap at 59, no hour/day rollover). */
export function normalizeLeadTimeParts(parts: LeadTimeParts): LeadTimeParts {
  return {
    days: Math.min(
      Math.max(0, Math.floor(parts.days)),
      LEAD_TIME_SEGMENT_MAX
    ),
    hours: Math.min(
      Math.max(0, Math.floor(parts.hours)),
      LEAD_TIME_SEGMENT_MAX
    ),
    minutes: Math.min(
      Math.max(0, Math.floor(parts.minutes)),
      LEAD_TIME_MINUTES_MAX
    ),
  };
}

/** Clamp minutes to 59 (and segment maxes); call on blur or save. */
export function normalizeLeadTimeDigits(digits: string): string {
  const cleaned = digits.replace(/[^0-9]/g, "").slice(-6);
  if (!cleaned) return "";
  return partsToDigits(normalizeLeadTimeParts(digitsToParts(cleaned)));
}

const LEGACY_LEAD_TIME_MINUTES: Record<string, number> = {
  "same day": 0,
  "1 business day": 24 * 60,
  "2-3 business days": 2 * 24 * 60,
  "1 week": 7 * 24 * 60,
  "2 weeks": 14 * 24 * 60,
  "3-4 weeks": 21 * 24 * 60,
  "4+ weeks": 28 * 24 * 60,
  "special order": 0,
};

const EMPTY_PARTS: LeadTimeParts = { days: 0, hours: 0, minutes: 0 };

function pad2(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0").slice(-2);
}

export function partsToDigits(parts: LeadTimeParts): string {
  const { days, hours, minutes } = parts;
  if (days === 0 && hours === 0 && minutes === 0) return "";
  const full = `${pad2(days)}${pad2(hours)}${pad2(minutes)}`;
  return full.replace(/^0+/, "") || "";
}

export function digitsToParts(digits: string): LeadTimeParts {
  const padded = digits.replace(/[^0-9]/g, "").padStart(6, "0").slice(-6);
  return {
    days: parseInt(padded.slice(0, 2), 10),
    hours: parseInt(padded.slice(2, 4), 10),
    minutes: parseInt(padded.slice(4, 6), 10),
  };
}

function partsFromTotalMinutes(totalMinutes: number): LeadTimeParts {
  const safe = Math.max(0, Math.floor(totalMinutes));
  const days = Math.floor(safe / (24 * 60));
  const remainder = safe % (24 * 60);
  return {
    days: Math.min(days, 99),
    hours: Math.floor(remainder / 60),
    minutes: remainder % 60,
  };
}

function parseLegacyText(input: string): LeadTimeParts | null {
  const trimmed = input.trim();
  if (!trimmed) return { ...EMPTY_PARTS };

  const legacy = LEGACY_LEAD_TIME_MINUTES[trimmed.toLowerCase()];
  if (legacy !== undefined) return partsFromTotalMinutes(legacy);

  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (colonMatch) {
    return {
      days: parseInt(colonMatch[1], 10),
      hours: parseInt(colonMatch[2], 10),
      minutes: parseInt(colonMatch[3], 10),
    };
  }

  let days = 0;
  let hours = 0;
  let minutes = 0;
  let matched = false;

  const unitPattern =
    /(\d+(?:\.\d+)?)\s*(days?|d|weeks?|w|hours?|hrs?|h|minutes?|mins?|m)\b/gi;
  let unitMatch: RegExpExecArray | null;
  while ((unitMatch = unitPattern.exec(trimmed)) !== null) {
    matched = true;
    const amount = parseFloat(unitMatch[1]);
    const unit = unitMatch[2].toLowerCase();
    if (unit.startsWith("w")) days += amount * 7;
    else if (unit.startsWith("d")) days += amount;
    else if (unit === "h" || unit.startsWith("h")) hours += amount;
    else minutes += amount;
  }

  if (!matched) {
    const pattern = /(\d+)\s*(d|h|m)\b/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(trimmed)) !== null) {
      matched = true;
      const n = parseInt(match[1], 10);
      const u = match[2].toLowerCase();
      if (u === "d") days += n;
      else if (u === "h") hours += n;
      else minutes += n;
    }
  }

  if (!matched) {
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      return partsFromTotalMinutes(parseFloat(trimmed) * 24 * 60);
    }
    return null;
  }

  return {
    days: Math.min(Math.floor(days), 99),
    hours: Math.min(Math.floor(hours), 99),
    minutes: Math.min(Math.floor(minutes), LEAD_TIME_SEGMENT_MAX),
  };
}

/** Parse stored API value into day/hour/minute parts. */
export function parseLeadTimeStored(
  stored: string | null | undefined
): LeadTimeParts {
  const trimmed = (stored ?? "").trim();
  if (!trimmed) return { ...EMPTY_PARTS };

  if (/^\d{6}$/.test(trimmed)) {
    return digitsToParts(trimmed);
  }

  if (/^\d{4}$/.test(trimmed)) {
    return digitsToParts(`00${trimmed}`);
  }

  const daysAndTime = trimmed.match(/^(\d+)d:(\d{4})$/i);
  if (daysAndTime) {
    const hhmm = daysAndTime[2];
    const timeParts = digitsToParts(`00${hhmm}`);
    return {
      days: parseInt(daysAndTime[1], 10) || 0,
      hours: timeParts.hours,
      minutes: timeParts.minutes,
    };
  }

  const legacy = parseLegacyText(trimmed);
  if (legacy) return legacy;

  return { ...EMPTY_PARTS };
}

/** Canonical storage: 6-digit DDHHMM (e.g. 021430 = 2d 14h 30m). */
export function leadTimeDigitsToStorage(digits: string): string | null {
  const cleaned = digits.replace(/[^0-9]/g, "");
  if (!cleaned) return null;
  const padded = cleaned.padStart(6, "0").slice(-6);
  if (padded === "000000") return null;
  return padded;
}

export function formatLeadTimeStorage(parts: LeadTimeParts): string | null {
  return leadTimeDigitsToStorage(
    partsToDigits(normalizeLeadTimeParts(parts))
  );
}

export function normalizeLeadTimeStored(
  stored: string | null | undefined
): string {
  return formatLeadTimeStorage(parseLeadTimeStored(stored)) ?? "";
}

/** Stored value → calculator-style digits for MaskedLeadTimeInput. */
export function leadTimeToFormDigits(
  stored: string | null | undefined
): string {
  return partsToDigits(parseLeadTimeStored(stored));
}

/** Display as 02d : 14h : 30m */
export function formatVendorLeadTimeDisplay(
  stored: string | null | undefined
): string {
  const parts = parseLeadTimeStored(stored);
  if (parts.days === 0 && parts.hours === 0 && parts.minutes === 0) {
    const trimmed = (stored ?? "").trim();
    return trimmed || "N/A";
  }
  return `${pad2(parts.days)}d : ${pad2(parts.hours)}h : ${pad2(parts.minutes)}m`;
}
